import React from 'react';
import { get } from 'lodash-es';
import { getModelState } from 'client/data/luckdragon/model';
import {
  VehicleModel,
  buildPopularStylesPath,
  buildVehiclePath,
  FeatureSpecsPaths,
  buildFuelCostsPath,
  buildEvTestedDataPath,
  buildMakeModelDefaultYear,
  buildNewTmvPricingPath,
} from 'client/data/models/vehicle-v2';
import { AppraisalModel, buildAllStylesAppraisalFilteredPath } from 'client/data/models/appraisal';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { getIfEvTaxCreditAmountPath, IncentiveModel } from 'client/data/models/incentives';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { InventoryModel } from 'client/data/models/inventory';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { PageModel } from 'client/data/models/page';
import { getParamsWithMakes } from 'client/utils/router-params';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { setCriticalCss } from 'client/actions/critical-css';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { BUILD_PRICE_AD, INCENTIVES_AD, INSIGHT_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { getQuery } from 'client/utils/location';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getCoreUrlPattern } from 'site-modules/shared/utils/core-page/url-pattern';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'site-modules/shared/components/native-ad/utils/utils';
import { getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import {
  getInitialSearchFilter,
  getUnknownLocationSearchFilter,
  MINI_INVENTORY_COUNT,
} from 'site-modules/shared/utils/mini-inventory/mini-inventory';
import { PAGE_DISPLAY_NAME, URL_PATH } from 'site-modules/features-specs/constants/features-specs';
import { EV_TESTED_DATA_START_DATE } from 'site-modules/shared/constants/range-and-cost';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import { setVenomTestHeader } from 'client/actions/venom-test-header';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { VisitorModel } from 'client/data/models/visitor';
import { decoratorParams } from 'site-modules/shared/pages/decorators/decorator-params';
import { EdmundsDefaultDecorator } from 'site-modules/shared/pages/decorators/edmunds-default';
import { FeaturesSpecs } from './features-specs';

export const pageOptions = {
  name: 'model_core_feature_specs',
  category: 'model_core',
  chunkName: 'model_core_feature_specs',
  preloadAboveFold: async (store, props) => {
    const params = props.match.params;
    const { make, model, year, styleId, submodel } = params;

    store.dispatch(setVenomTestHeader({ paf: 1 }));
    const query = getQuery(props.location);

    const preloader = new VehiclePreloader(store, { ...props, params });

    preloader.set('searchResultsFilter', InventoryModel, {
      ...getInitialSearchFilter({ makeSlug: make, modelSlug: model, year }),
      ...getFilterFromLocation(query),
    });
    preloader.set(URL_CONTEXT_PATH, UrlModel, {
      urlPattern: getCoreUrlPattern(year, submodel, 'features-specs'),
    });
    preloader.resolve(buildPopularStylesPath(params), VehicleModel);
    preloader.resolve(buildAllStylesAppraisalFilteredPath(params), AppraisalModel);
    const styleOverviewPath = styleId
      ? FeatureSpecsPaths.buildFeaturesOverviewPathForStyleId(styleId)
      : FeatureSpecsPaths.buildFeaturesOverviewPathForDefaultStyle(params);
    preloader.resolve(styleOverviewPath, VehicleModel);

    preloader.set('pageAdsListSeparatedCalls', NativeAdsCreativeConfigModel, [
      INCENTIVES_AD.AD_NAME,
      BUILD_PRICE_AD.AD_NAME,
      INSIGHT_AD.AD_NAME,
    ]);

    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
      const isUsed = isUsedPubState(makeModelSubmodelYear.pubStates);

      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_model_core_feature_specs`,
          categoryName: `${isUsed ? 'used' : 'new'}_model_core`,
        })
      );
      const popularStyles = modelState.get(buildPopularStylesPath(params), VehicleModel);
      const currentStyleId = styleId || get(popularStyles, '[0].id');
      const styleFeatures = modelState.get(
        FeatureSpecsPaths.buildFeaturesOverviewPathForStyleId(currentStyleId),
        VehicleModel
      );
      const isEv = styleFeatures?.styleAttributes?.electric || styleFeatures?.styleAttributes?.pluginElectric;
      const disableCriticalCssOnFs = modelState.get('disableCriticalCssOnFs', FeatureFlagModel);
      const criticalCss = disableCriticalCssOnFs
        ? false
        : {
            isStylePage: !!styleId,
            isEv,
            paf: true,
          };
      store.dispatch(setCriticalCss(criticalCss));
      return preloader.load();
    });
  },
  preload: (store, props) => {
    store.dispatch(setVenomTestHeader({ paf: 0 }));
    const params = props.match.params;
    const { make, model, year, styleId, submodel } = params;
    const query = getQuery(props.location);
    const { mobile } = store.getState();
    const preloader = new VehiclePreloader(store, { ...props, params });

    preloader.set(URL_CONTEXT_PATH, UrlModel, {
      urlPattern: getCoreUrlPattern(year, submodel, 'features-specs'),
    });
    preloader.resolve(buildPopularStylesPath(params), VehicleModel);
    preloader.resolve(FeatureSpecsPaths.buildFeaturesOverviewPathForDefaultStyle(params), VehicleModel);

    preloader.resolve('location', VisitorModel);

    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
      const isUsed = isUsedPubState(makeModelSubmodelYear.pubStates);

      const isInternational = isUnknownZip(modelState.get('location', VisitorModel));
      preloader.set('searchResultsFilter', InventoryModel, {
        ...getUnknownLocationSearchFilter(isInternational),
        ...getInitialSearchFilter({ makeSlug: make, modelSlug: model, year }),
        ...getFilterFromLocation(query),
        pageSize: MINI_INVENTORY_COUNT[!!mobile],
      });
      preloader.resolve('searchResults', InventoryModel);

      const vehiclePageContext = modelState.get('vehicle', PageModel);
      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_model_core_feature_specs`,
          categoryName: `${isUsed ? 'used' : 'new'}_model_core`,
        })
      );
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      setAdsListToPreload(
        preloader,
        store.getState(),
        [INCENTIVES_AD.AD_NAME, BUILD_PRICE_AD.AD_NAME, INSIGHT_AD.AD_NAME],
        vehiclePageContext
      );

      return preloader.load().then(state => {
        const popularStyles = state.get(buildPopularStylesPath(params), VehicleModel);
        const currentStyleId = styleId || get(popularStyles, '[0].id');

        preloader.resolve(FeatureSpecsPaths.buildStyleFeaturesPath(currentStyleId), VehicleModel);
        preloader.resolve(buildFuelCostsPath(currentStyleId), VehicleModel);
        preloader.resolve(getIfEvTaxCreditAmountPath(currentStyleId, isUsed), IncentiveModel);

        if (isUsed) {
          preloader.resolve(buildAllStylesAppraisalFilteredPath(params), AppraisalModel);
        } else {
          preloader.resolve(buildNewTmvPricingPath(currentStyleId, false), VehicleModel);
          preloader.resolve(
            getUnifiedLeadFormDealerCountV2Path({
              makeSlug: make,
              modelSlug: model,
              strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
            }),
            UnifiedLeadFormModel
          );
        }

        if (!isUsed && !DEALERLESS_OEM.includes(params.make)) {
          preloader.resolve(
            buildTrimSummariesPath(getParamsFromVehicle(makeModelSubmodelYear)),
            InventoryCoreConfigurationsModel
          );
        }

        return preloader.load().then(stateModel => {
          const styleFeatures = stateModel.get(FeatureSpecsPaths.buildStyleFeaturesPath(currentStyleId), VehicleModel);

          if (params.year >= EV_TESTED_DATA_START_DATE && get(styleFeatures, 'styleAttributes.electric')) {
            preloader.resolve(buildEvTestedDataPath([currentStyleId]), VehicleModel);
          }

          const siteServedAds = stateModel.get(
            getCreativeConfigAdPathByVehicle(mobile, vehiclePageContext),
            NativeAdsCreativeConfigModel
          );
          const isEv = styleFeatures?.styleAttributes?.electric || styleFeatures?.styleAttributes?.pluginElectric;
          store.dispatch(
            setCriticalCss({
              isStylePage: !!styleId,
              ...siteServedAdsCriticalCss(siteServedAds, [INCENTIVES_AD.AD_NAME]),
              isEv,
              isUsed,
              paf: false,
            })
          );
          return preloader.load();
        });
      });
    });
  },
  seo: (state, props) => {
    const params = props.match.params;
    const { styleId } = params;
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);

    const popularStyles = modelState.get(buildPopularStylesPath(params), VehicleModel);
    const selectedStyle = popularStyles && popularStyles.find(({ id }) => id === parseInt(styleId, 10));

    const seoObject = {};

    const vehicleName = buildDisplayVehicle({
      makeModelSubmodelYear,
      params,
      style: selectedStyle,
      showTrim: true,
    });
    const breadcrumbs = buildCoreBreadcrumbs({
      makeModelSubmodelYear,
      params,
      defaultYear,
      pageDisplayName: PAGE_DISPLAY_NAME,
      urlPath: getUrlPathFromParams(params, URL_PATH),
      includeLastLink: true,
    });

    seoObject.title = `${vehicleName} Specs & Features | Edmunds`;
    seoObject.description = `Detailed specs and features for the ${vehicleName} including dimensions, horsepower, engine, capacity, fuel economy, transmission, engine type, cylinders, drivetrain and more.`;
    seoObject.keywords = '';
    seoObject.jsonld = [getBreadcrumbSchema(breadcrumbs)];

    return seoObject;
  },
  component: props => <FeaturesSpecs {...props} params={getParamsWithMakes(props)} />,
  options: {
    hasVehicleContext: true,
  },
  decorator: decoratorParams(EdmundsDefaultDecorator),
};

export default pageDefinition(pageOptions);
